import { PageWrapper } from '../../components/PageWrapper';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import OtpInput from 'react18-input-otp';
import { PHONE_PASSWORD_LOGIN, PHONE_PASSWORD_CREATE } from '../../constants/routes';
import { formatPhoneNumber } from '../../utils/phoneNumber';
import { changePassword } from '../../actions/userPhoneAction';
import useAxios from '../../axios/useAxios';
import { notifySuccess } from '../../utils/Notify';
import { auth } from '../../firebase/firebaseConfig';
import { BeatLoader } from 'react-spinners';
import { posthogPhoneNumberEvent } from '../../posthog/phoneNumberEvent';
import { POSTHOG_CAPTURE_PHONE_SIGN_UP_SUCCESS } from '../../posthog/constants';
export const PhonePasswordConfirm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const axiosInstance = useAxios();

  const {
    phoneNumber,
    isForgotPassword,
    isVerified,
    isNewUser,
    password,
  } = location.state || {};
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect if no phone number, not verified, or no password
  useEffect(() => {
    if (!phoneNumber || !isVerified || !password) {
      navigate(PHONE_PASSWORD_CREATE);
    }
  }, [phoneNumber, isVerified, password, navigate]);

  const displayNumber = phoneNumber ? formatPhoneNumber(phoneNumber) : '';

  // Handle password submission
  const handleSubmit = async () => {
    if (confirmPassword.length !== 6) {
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setConfirmPassword('');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // For new users: use phoneNumberSignUp to create account
      // For existing users (forgot password): use changePassword to update password
      if (isNewUser === true) {
        console.info('Creating new user account with phoneNumberSignUp');
        const payload = {
          user: {
            phoneNumber,
            password,
          },
          order: null,
          lastLoginAt: auth?.currentUser?.metadata?.lastLoginAt,
          isPWA: false,
        };

        const response = await axiosInstance.post(
          'user/phoneNumberSignUp',
          payload,
        );

        if (response.status === 201) {
          notifySuccess('Compte créé avec succès');
          posthogPhoneNumberEvent(POSTHOG_CAPTURE_PHONE_SIGN_UP_SUCCESS, { phoneNumber: phoneNumber })
          setTimeout(() => {
            navigate(PHONE_PASSWORD_LOGIN, {
              state: { phoneNumber, userExists: true },
            });
          }, 1500);
        } else {
          setError(response?.data?.message || 'Création de compte échouée');
          setLoading(false);
        }
      } else {
        // Existing user: change password
        console.info('Changing password for existing user with changePassword');
        const result = await changePassword(
          axiosInstance,
          phoneNumber,
          password,
          () => {}, // setLockUntil callback (not needed here)
        );

        if (result.success) {
          notifySuccess('Mot de passe modifié avec succès');
          setTimeout(() => {
            navigate(PHONE_PASSWORD_LOGIN, {
              state: { phoneNumber, userExists: true },
            });
          }, 1500);
        } else {
          setError(result?.serverError || 'Erreur lors de la modification du mot de passe');
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Password change error:', error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors de la modification du mot de passe';
      setError(message);
      setLoading(false);
    }
  };

  // Auto-submit when password is complete
  useEffect(() => {
    if (confirmPassword.length === 6) {
      handleSubmit();
    }
  }, [confirmPassword]);

  return (
    <PageWrapper
      title={isForgotPassword ? "🔑 Confirmation du nouveau mot de passe" : "🔑 Confirmation du mot de passe" }
      onGoBack={() => navigate(PHONE_PASSWORD_CREATE, {
        state: { phoneNumber, isForgotPassword, isVerified }
      })}
    >
      <div className="flex flex-col items-center justify-center gap-6">
        {/* Confirmation password input */}
        <div className="w-full flex justify-center">
          {loading ? (
            <BeatLoader color="#183153" size={20} />
          ) : (
            <OtpInput
            value={confirmPassword}
            onChange={setConfirmPassword}
            numInputs={6}
            isInputNum={true}
            isDisabled={loading}
            shouldAutoFocus={true}
            inputStyle="password-input"
            isInputSecure={true}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            separator={<span className="otp-separator" />}
            containerStyle="otp-container"
            />
          )}
        </div>

        {/* Loading indicator */}
        {loading && (
          <p className="text-gray-500 text-lg mt-2">Modification en cours...</p>
        )}

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-2 w-full">
            <p className="text-red-600 text-lg font-semibold text-center">{error}</p>
          </div>
        )}

        {/* Instructions */}
        {!error && !loading && (
          <p className="text-gray-500 text-xl mt-2 text-center">
            Saisis les 6 chiffres de ton mot de passe
          </p>
        )}
      </div>
    </PageWrapper>
  );
};

